import { roleColorValues, type RoleToken } from './roleTokenValues';
import { useThemeMode } from './useThemeMode';

/**
 * The current mode's role token as a parseable hex string.
 *
 * Use this instead of `Colors.role.*` ONLY where the value crosses a
 * color-math boundary: on web `Colors.role.*` carries `var()` strings, and
 * anything that parses colours in JS at render time chokes on them, throwing
 * `Unable to parse color from string: var(--x)`. In react-native-paper that
 * throw happens during render, so it takes the whole screen down through the
 * error boundary rather than merely rendering the wrong colour.
 *
 * The known offenders, all of which pass the value into the `color` library
 * (verified against react-native-paper 5.14.5 by calling each helper with a
 * `var()` and watching it throw; `eslint-local-rules/no-role-token-in-paper-color.js`
 * enforces the list):
 *
 *   - `Button`      `buttonColor` / `textColor`. `textColor` is returned
 *                   untouched by `getButtonColors`, but `Button.js` then does
 *                   `Color(textColor).alpha(0.12)` for the ripple unless
 *                   `rippleColor` is also given.
 *   - `IconButton`  `iconColor` (`getIconButtonColor`, every mode)
 *   - `Checkbox` / `RadioButton`  `color` (the *checked* colour only:
 *                   `Color(checkedColor).fade(0.32)`). `uncheckedColor` is
 *                   returned as-is and is safe. `RadioButton.Android` is
 *                   affected too - it has no colour maths of its own but
 *                   imports `Checkbox/utils`, so it goes through the same
 *                   call. `rippleColor` does NOT rescue a `Checkbox.Item`:
 *                   that prop lands on the row's TouchableRipple and is never
 *                   forwarded to the inner Checkbox.
 *   - `Switch`      `color`
 *   - `TextInput`   `outlineColor`. `getOutlinedOutlineInputColor` runs
 *                   `Color(customOutlineColor).alpha()` *before* the guard
 *                   that returns it untouched, so it throws even though the
 *                   value is used verbatim. `activeOutlineColor`,
 *                   `activeUnderlineColor`, `underlineColor` and `textColor`
 *                   are all safe on web (the one `Color()` on the active
 *                   colour is behind a `Platform.OS === 'android'` branch,
 *                   and native never sees a `var()`).
 *   - `Chip`        `style.backgroundColor` — note this one is a STYLE, not a
 *                   prop: Paper flattens the style and mixes that background
 *                   with a theme colour.
 *
 * So "style objects are always safe" is not a rule to rely on. A style is safe
 * only until it reaches a component that reads a colour back out of it and does
 * arithmetic. Before handing any Paper component a colour, check whether it
 * ends up inside a `Color(...)` call in that component's `utils.js` /
 * `helpers.js`. Conversely `Button`/`IconButton` `style.backgroundColor` IS
 * safe: those flatten the style only to read `border*Radius`.
 *
 * Non-Paper `color` props are safe: expo vector icons and the Crop Circle SVG
 * icons put the value straight into a style, and react-native-web passes
 * anything starting with `var(` through to CSS untouched.
 *
 * Everywhere else keeps using `Colors.role.*`: the CSS variable is what lets
 * those surfaces theme without re-rendering. This hook re-renders its component
 * when the mode flips, which is exactly the cost `var()` avoids, so keep it at
 * the boundaries that need it.
 */
export const useRoleHex = (token: RoleToken): string =>
  roleColorValues[useThemeMode()][token];
